import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Typography,
  CircularProgress,
  Tooltip,
  Fab,
  Badge,
  Button,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import MinimizeIcon from '@mui/icons-material/Minimize';
import CallEndIcon from '@mui/icons-material/CallEnd';
import ChatIcon from '@mui/icons-material/Chat';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DownloadIcon from '@mui/icons-material/Download';
import ScreenshotMonitorIcon from '@mui/icons-material/ScreenshotMonitor';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import { fTime, fToNow } from 'src/utils/formatTime';
import { Z_INDEX } from 'src/constants/zIndex';
import axios from 'src/utils/axios';

// The widget itself sits at Z_INDEX.MAX (must float above all app UI). MUI's
// Tooltip portals default to theme.zIndex.tooltip (1500) — well below that —
// so every Tooltip in this file needs an explicit bump above the widget's own
// z-index, or it silently renders behind the widget instead of on top of it.
const TOOLTIP_POPPER_PROPS = {
  popper: { style: { zIndex: Z_INDEX.MAX + 1 } },
} as const;

// Soft ambient shadow shared by every widget surface — mirrors the theme's
// customShadows.z24. This widget renders in a separate React root on
// document.body (see useConnectChat), OUTSIDE the app's ThemeProvider, so the
// Minimal-template theme extensions aren't available; use a literal value.
// Keep in sync with ChatLauncher's panel shadow.
const PANEL_SHADOW =
  '0 0 4px 0 rgba(145, 158, 171, 0.24), 0 24px 48px 0 rgba(145, 158, 171, 0.24)';

type MessageStatus = 'SENDING' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';

// Ordering used to make sure a receipt event never downgrades a message's status
// (e.g. a late "delivered" event arriving after "read" should be a no-op).
const STATUS_RANK: Record<MessageStatus, number> = {
  SENDING: 0,
  FAILED: 0,
  SENT: 1,
  DELIVERED: 2,
  READ: 3,
};

// Consecutive messages from the same participant within this window are grouped
// together visually (sender label + timestamp shown once per group, not per message).
const GROUP_WINDOW_MS = 5 * 60 * 1000;

interface ChatMessage {
  id: string;
  participantRole: string;
  displayName?: string;
  contentType: string;
  content: string;
  absoluteTime?: string;
  attachmentId?: string;
  attachmentName?: string;
  status?: MessageStatus;
}

interface Props {
  contactId: string;
  participantId: string;
  participantToken: string;
  region: string;
  // 'agent' (default) is a normal live-agent chat. 'report_issue' skips the
  // agent queue entirely (see the Connect flow's check_intent block) and
  // shows a "Submit Ticket" control instead of relying on an agent to
  // respond — the customer describes their issue + attaches files as
  // regular chat messages/attachments, then submits to create a Jira ticket.
  intent?: 'agent' | 'report_issue';
  // Allow the chat bubble to be dismissed (hidden with a restore button).
  // Internal support users can dismiss the chat; external users see no
  // dismiss affordance when this is false.
  dismissible?: boolean;
  // Fires exactly once whenever the chat is truly over — explicit close,
  // agent ended it, or the connection dropped. The hook uses this to swap
  // this widget out for the ChatLauncher (with freshly fetched history) in
  // the same shared root, rather than tearing the whole thing down.
  onEnd: (transcript: ChatMessage[]) => void;
  // Fires whenever this component unmounts, for ANY reason — the end→Launcher
  // swap, or an uncaught error elsewhere. The hook uses this as a safety net
  // to clear its "something is currently shown" ref if the swap didn't
  // happen for some reason, so a crash can never leave the floating chat
  // button permanently unable to reopen.
  onUnmount?: () => void;
}

// amazon-connect-chatjs is loaded as a global (window.connect), not an ES module,
// so we intentionally use `any` at the SDK boundary rather than fight a namespace
// that isn't imported anywhere in this file.
type ChatSessionType = any;

function renderReceiptIcon(status?: MessageStatus): React.ReactElement | null {
  if (status === 'READ') {
    return (
      <Tooltip title="Read" slotProps={TOOLTIP_POPPER_PROPS}>
        <DoneAllIcon sx={{ fontSize: 14, color: 'primary.main' }} />
      </Tooltip>
    );
  }
  if (status === 'DELIVERED') {
    return (
      <Tooltip title="Delivered" slotProps={TOOLTIP_POPPER_PROPS}>
        <DoneAllIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
      </Tooltip>
    );
  }
  if (status === 'SENT') {
    return (
      <Tooltip title="Sent" slotProps={TOOLTIP_POPPER_PROPS}>
        <DoneIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
      </Tooltip>
    );
  }
  if (status === 'SENDING') {
    return <CircularProgress size={10} thickness={6} />;
  }
  if (status === 'FAILED') {
    return (
      <Tooltip title="Failed to send" slotProps={TOOLTIP_POPPER_PROPS}>
        <ErrorOutlineIcon sx={{ fontSize: 14, color: 'error.main' }} />
      </Tooltip>
    );
  }
  return null;
}

export default function ConnectChatWidget({
  contactId,
  participantId,
  participantToken,
  region,
  intent = 'agent',
  dismissible = false,
  onEnd,
  onUnmount,
}: Props): React.ReactElement {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [connecting, setConnecting] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agentTyping, setAgentTyping] = useState(false);
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState<
    string | null
  >(null);
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [chatEnded, setChatEnded] = useState(false);
  const [submittingTicket, setSubmittingTicket] = useState(false);
  const [ticketError, setTicketError] = useState<string | null>(null);
  const [ticketKey, setTicketKey] = useState<string | null>(null);
  const [closeCountdown, setCloseCountdown] = useState<number | null>(null);
  const [endedMessage, setEndedMessage] = useState<string>(
    'This conversation has ended.'
  );
  // Minimized: the widget collapses to a small pill so the user can navigate
  // to other pages while the ChatJS connection, messages, and typing
  // indicators keep running normally in the background — nothing here
  // unmounts, only the JSX branch we render changes.
  const [minimized, setMinimized] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  // Guards against onEnd firing more than once — explicit close and the
  // internal onEnded/onConnectionBroken SDK events can otherwise race.
  const endedCalledRef = useRef(false);
  const sessionRef = useRef<ChatSessionType | null>(null);
  const messagesRef = useRef<ChatMessage[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const connectedRef = useRef(false);
  const chatEndedRef = useRef(false);
  // Mirrors `minimized` for the onMessage SDK callback, which is registered
  // once inside the init effect and would otherwise see a stale value.
  const minimizedRef = useRef(false);
  // Message IDs whose "read" receipt was deferred because they arrived
  // while minimized — the customer hasn't actually seen them yet, so
  // telling Connect/the agent "read" immediately would be a false signal.
  // Flushed the moment the widget is restored.
  const pendingReadReceiptsRef = useRef<string[]>([]);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // A ticket key only exists once create_ticket returned success. From that
  // point the header control must stop offering to "Cancel": there is no
  // cancel path, and its onClick ends the chat and finalizes the session.
  const ticketSubmitted = ticketKey !== null;

  // Keep messagesRef in sync with state for the cleanup handler
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Ref mirror of chatEnded for synchronous checks inside callbacks (state
  // alone can be stale between the SDK event firing and the next render).
  useEffect(() => {
    chatEndedRef.current = chatEnded;
  }, [chatEnded]);

  useEffect(() => {
    minimizedRef.current = minimized;
    if (!minimized) {
      setUnreadCount(0);
      if (pendingReadReceiptsRef.current.length > 0) {
        const messageIds = pendingReadReceiptsRef.current;
        pendingReadReceiptsRef.current = [];
        messageIds.forEach((messageId) => {
          sessionRef.current
            ?.sendMessageReceipt({ event: 'read', messageId })
            .catch(() => {
              /* best-effort */
            });
        });
      }
    }
  }, [minimized]);

  // Always call the latest onUnmount without re-running the unmount effect
  // itself on every render (that effect must only fire once, on unmount).
  const onUnmountRef = useRef(onUnmount);
  useEffect(() => {
    onUnmountRef.current = onUnmount;
  }, [onUnmount]);

  useEffect(() => {
    return () => {
      onUnmountRef.current?.();
    };
  }, []);

  // Auto-scroll to the latest message (or the typing indicator) as the transcript grows
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });
  }, [messages.length, agentTyping]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const updateReceiptStatus = useCallback(
    (event: any, status: MessageStatus) => {
      const data = event?.data || event;
      let targetId: string | undefined;
      try {
        const parsed = JSON.parse(data?.Content || '{}');
        targetId = parsed.messageId;
      } catch {
        targetId = undefined;
      }
      if (!targetId) {
        return;
      }
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== targetId) {
            return m;
          }
          const currentRank = STATUS_RANK[m.status || 'SENT'] ?? 0;
          const nextRank = STATUS_RANK[status] ?? 0;
          return nextRank < currentRank ? m : { ...m, status };
        })
      );
    },
    []
  );

  // Initialize chat session
  useEffect(() => {
    let cancelled = false;

    async function init(): Promise<void> {
      try {
        // amazon-connect-chatjs is a global namespace (window.connect), not an ES module
        await new Promise<void>((resolve) => {
          const checkConnect = (): void => {
            if ((window as any).connect?.ChatSession) {
              resolve();
            } else {
              setTimeout(checkConnect, 100);
            }
          };
          checkConnect();
        });

        const chatSession = (window as any).connect.ChatSession.create({
          chatDetails: {
            contactId,
            participantId,
            participantToken,
          },
          type: 'CUSTOMER',
          options: { region },
        } as any);

        if (cancelled) {
          chatSession.disconnectParticipant();
          return;
        }

        sessionRef.current = chatSession;

        await chatSession.connect();

        // Handle incoming messages and attachments
        chatSession.onMessage((message: any) => {
          try {
            const msg = message.data || message;

            if (msg.Type === 'MESSAGE') {
              // Own sent messages echo back through onMessage too — dedupe by Id so a
              // message never renders twice (the optimistic local echo already has it).
              setMessages((prev) => {
                if (prev.some((m) => m.id === msg.Id)) {
                  return prev;
                }
                const newMsg: ChatMessage = {
                  id: msg.Id || String(Date.now()),
                  participantRole: msg.ParticipantRole || 'CUSTOMER',
                  displayName: msg.DisplayName,
                  contentType: msg.ContentType || 'text/plain',
                  content: msg.Content || '',
                  absoluteTime: msg.AbsoluteTime,
                  status:
                    msg.ParticipantRole === 'CUSTOMER' ? 'SENT' : undefined,
                };
                return [...prev, newMsg];
              });

              if (msg.ParticipantRole === 'AGENT') {
                // Agent sent a real message — the typing indicator is stale now.
                setAgentTyping(false);
                if (typingTimeoutRef.current) {
                  clearTimeout(typingTimeoutRef.current);
                }
                // The message DID arrive on the client either way, so
                // "delivered" is always accurate. "Read" is only accurate
                // if the customer can actually see it right now — while
                // minimized, defer it until they restore the widget
                // (flushed in the minimized-state effect above) rather
                // than telling the agent "read" on a message nobody's
                // looked at yet.
                sessionRef.current
                  ?.sendMessageReceipt({
                    event: 'delivered',
                    messageId: msg.Id,
                  })
                  .catch(() => {
                    /* best-effort — receipts are a UX nicety, not required for delivery */
                  });
                if (minimizedRef.current) {
                  setUnreadCount((prev) => prev + 1);
                  pendingReadReceiptsRef.current.push(msg.Id);
                } else {
                  sessionRef.current
                    ?.sendMessageReceipt({ event: 'read', messageId: msg.Id })
                    .catch(() => {
                      /* best-effort */
                    });
                }
              }
            } else if (msg.Type === 'ATTACHMENT') {
              setMessages((prev) => {
                if (prev.some((m) => m.id === msg.Id)) {
                  return prev;
                }
                // ChatJS delivers attachment metadata as an array (Attachments[0]),
                // not flat AttachmentId/AttachmentName fields on the message itself.
                const attachment = msg.Attachments?.[0] || {};
                const attachMsg: ChatMessage = {
                  id: msg.Id || String(Date.now()),
                  participantRole: msg.ParticipantRole || 'CUSTOMER',
                  displayName: msg.DisplayName,
                  contentType:
                    attachment.ContentType || 'application/octet-stream',
                  content: `File: ${attachment.AttachmentName || 'attachment'}`,
                  absoluteTime: msg.AbsoluteTime,
                  attachmentId: attachment.AttachmentId,
                  attachmentName: attachment.AttachmentName,
                  status:
                    msg.ParticipantRole === 'CUSTOMER' ? 'SENT' : undefined,
                };
                return [...prev, attachMsg];
              });
              if (msg.ParticipantRole === 'AGENT' && minimizedRef.current) {
                setUnreadCount((prev) => prev + 1);
              }
            }
          } catch {
            // A malformed/unexpected event payload must never throw uncaught
            // out of an SDK callback — that's outside React's render cycle,
            // so nothing catches it and it can crash the whole widget silently.
          }
        });

        // Typing indicator — Connect doesn't send an explicit "stopped typing" event,
        // so we clear it ourselves after a quiet period.
        chatSession.onTyping((event: any) => {
          try {
            const data = event?.data || event;
            if (data?.ParticipantRole === 'AGENT') {
              setAgentTyping(true);
              if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
              }
              typingTimeoutRef.current = setTimeout(
                () => setAgentTyping(false),
                10000
              );
            }
          } catch {
            // See onMessage — never let a callback throw uncaught.
          }
        });

        // Read/delivered receipts for messages WE sent
        chatSession.onDeliveredReceipt((event: any) => {
          try {
            updateReceiptStatus(event, 'DELIVERED');
          } catch {
            // See onMessage — never let a callback throw uncaught.
          }
        });
        chatSession.onReadReceipt((event: any) => {
          try {
            updateReceiptStatus(event, 'READ');
          } catch {
            // See onMessage — never let a callback throw uncaught.
          }
        });

        // Once we know the chat is over, tell ChatJS we're done rather than
        // leaving it to keep retrying its own internal reconnect/heartbeat
        // against a participant that's already gone — that's what produces
        // the background 403s on CreateParticipantConnection after the fact.
        // Also fires onEnd exactly once so the hook swaps this widget out
        // for the Launcher (with fresh history) regardless of WHO ended the
        // chat — agent, connection drop, or the customer themselves.
        const markEnded = (message: string): void => {
          setEndedMessage(message);
          setChatEnded(true);
          try {
            sessionRef.current?.disconnectParticipant();
          } catch {
            // Already gone server-side — exactly what we wanted.
          }
          if (!endedCalledRef.current) {
            endedCalledRef.current = true;
            onEnd(messagesRef.current);
          }
        };

        // The chat has ended — agent ended it, customer ended it elsewhere
        // (e.g. a different tab), or Connect closed it server-side. This is
        // the correct SDK event for "conversation is over" (business-level),
        // distinct from onConnectionBroken (transport-level). Show a clear
        // in-widget banner and lock the input rather than silently vanishing
        // or letting the user type into a dead session.
        chatSession.onEnded(() => {
          try {
            markEnded('This conversation has ended.');
          } catch {
            // See onMessage — never let a callback throw uncaught.
          }
        });

        // Handle connection established
        chatSession.onConnectionEstablished(() => {
          try {
            connectedRef.current = true;
            setConnecting(false);
          } catch {
            // See onMessage — never let a callback throw uncaught.
          }
        });

        // Handle low-level connection events
        chatSession.onConnectionBroken(() => {
          try {
            if (connectedRef.current) {
              // Transport dropped after we were connected — show it as an
              // ended state too rather than silently unmounting via onEnd().
              // The customer can still read the transcript and close manually.
              markEnded('Connection to support was lost.');
            } else {
              setError('Unable to connect to chat. Please try again.');
            }
            setConnecting(false);
          } catch {
            // See onMessage — never let a callback throw uncaught.
          }
        });
      } catch (e: any) {
        if (!cancelled) {
          setError(e.message || 'Failed to connect');
        }
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, [
    contactId,
    participantId,
    participantToken,
    region,
    onEnd,
    updateReceiptStatus,
  ]);

  // Awaitable single-message send. Kept separate from sendMessage() so
  // handleSubmitTicket can flush the composer and *wait* for it before
  // compiling the ticket description (see the composer-flush note there).
  const sendMessageText = useCallback(
    async (text: string): Promise<boolean> => {
      const msgText = text.trim();
      if (!msgText || !sessionRef.current || chatEndedRef.current) {
        return false;
      }

      const tempId = `pending-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`;
      const localMsg: ChatMessage = {
        id: tempId,
        participantRole: 'CUSTOMER',
        contentType: 'text/plain',
        content: msgText,
        status: 'SENDING',
      };
      setMessages((prev) => [...prev, localMsg]);

      try {
        const result: any = await sessionRef.current.sendMessage({
          contentType: 'text/plain',
          message: msgText,
        });
        const data = result?.data || result;
        // Swap the temp id for the real message Id so incoming receipt events
        // (which reference the real Id) can find and update this message.
        setMessages((prev) =>
          prev.map((m) =>
            m.id === tempId
              ? {
                  ...m,
                  id: data?.Id || tempId,
                  absoluteTime: data?.AbsoluteTime,
                  status: 'SENT',
                }
              : m
          )
        );
        return true;
      } catch {
        setMessages((prev) =>
          prev.map((m) => (m.id === tempId ? { ...m, status: 'FAILED' } : m))
        );
        return false;
      }
    },
    []
  );

  const sendMessage = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed) {
      return;
    }
    setInput('');
    void sendMessageText(trimmed);
  }, [input, sendMessageText]);

  const handleAttach = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !sessionRef.current || chatEndedRef.current) {
        return;
      }

      setUploadingFile(true);
      try {
        await sessionRef.current.sendAttachment({ attachment: file });
      } catch (err: any) {
        setError(err.message || 'Failed to send attachment');
      } finally {
        setUploadingFile(false);
      }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    []
  );

  const handleDownloadAttachment = useCallback(
    async (attachmentId: string, attachmentName?: string) => {
      if (!sessionRef.current || downloadingAttachmentId) {
        return;
      }
      setDownloadingAttachmentId(attachmentId);
      try {
        const blob = await sessionRef.current.downloadAttachment({
          attachmentId,
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = attachmentName || 'attachment';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (err: any) {
        setError(err.message || 'Failed to download attachment');
      } finally {
        setDownloadingAttachmentId(null);
      }
    },
    [downloadingAttachmentId]
  );

  const handleScreenshot = useCallback(async () => {
    if (!sessionRef.current || capturingScreenshot || chatEndedRef.current) {
      return;
    }
    setCapturingScreenshot(true);

    let stream: MediaStream | null = null;
    try {
      // Every call shows the browser's native share picker — there is no way
      // to remember consent for screen capture across calls (unlike camera/
      // mic). That's a deliberate browser platform decision, not a bug here.
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true });

      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      await video.play();
      // Give the video element a moment to actually paint a frame before
      // grabbing it — capturing immediately after play() can yield a blank
      // canvas on some browsers.
      await new Promise((resolve) => setTimeout(resolve, 300));

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) =>
            b ? resolve(b) : reject(new Error('Failed to encode screenshot')),
          'image/png'
        );
      });

      const file = new File([blob], `screenshot-${Date.now()}.png`, {
        type: 'image/png',
      });
      await sessionRef.current.sendAttachment({ attachment: file });
    } catch (err: any) {
      // User clicking "Cancel" in the share picker throws NotAllowedError —
      // that's an intentional no-op, not a real failure to surface.
      if (err?.name !== 'NotAllowedError') {
        setError(err.message || 'Failed to capture screenshot');
      }
    } finally {
      stream?.getTracks().forEach((track) => track.stop());
      setCapturingScreenshot(false);
    }
  }, [capturingScreenshot]);

  const handleDismiss = useCallback(() => {
    setHidden(true);
  }, []);

  const handleRestore = useCallback(() => {
    setHidden(false);
  }, []);

  const toggleFullScreen = useCallback(() => {
    setIsFullScreen((prev) => !prev);
    if (minimized) {
      setMinimized(false);
    }
  }, [minimized]);

  const handleEndChat = useCallback(() => {
    // If the conversation already ended (agent ended it, connection dropped),
    // the session is gone server-side — disconnectParticipant() on it would
    // just reject. Only call it when we're the ones ending an active chat.
    if (sessionRef.current && !chatEnded) {
      try {
        sessionRef.current.disconnectParticipant();
      } catch {
        // Already disconnected server-side — fine, proceed to finalize below.
      }
    }
    if (!endedCalledRef.current) {
      endedCalledRef.current = true;
      onEnd(messagesRef.current);
    }
  }, [onEnd, chatEnded]);

  // Compiles the customer's typed messages into a single description and
  // creates the Jira ticket. Shows the confirmation immediately from the
  // API response — doesn't wait on the Connect flow's polling relay (see
  // "Report Issue - Silent Queue Flow"), which exists as a safety-net to
  // eventually clean up the contact even if this widget crashes or the
  // user navigates away before this call completes.
  const handleSubmitTicket = useCallback(async (): Promise<void> => {
    // The composer is the natural place to type an issue, so people click
    // Submit Ticket with unsent text still sitting in it. Flush it as a real
    // message first (rather than only splicing it into the description) so
    // the words land in the Connect transcript and raw_transcript like any
    // other message.
    //
    // The already-sent messages are snapshotted *before* the flush: awaiting
    // the send gives React time to commit and re-sync messagesRef, so reading
    // the ref afterwards would already include the flushed text and counting
    // it again would duplicate it in the description.
    setTicketError(null);
    const priorMessages = messagesRef.current;
    const pending = input.trim();
    let flushed = '';

    if (pending) {
      setInput('');
      setSubmittingTicket(true);
      const sent = await sendMessageText(pending);
      if (!sent) {
        setSubmittingTicket(false);
        setInput(pending);
        setTicketError(
          "We couldn't send that message. Please check your connection and try again."
        );
        return;
      }
      flushed = pending;
    }

    const description = [
      ...priorMessages
        .filter(
          (m) =>
            m.participantRole === 'CUSTOMER' && m.contentType === 'text/plain'
        )
        .map((m) => m.content),
      flushed,
    ]
      .filter(Boolean)
      .join('\n')
      .trim();

    if (!description) {
      setSubmittingTicket(false);
      setTicketError('Please describe your issue before submitting.');
      return;
    }

    setSubmittingTicket(true);

    try {
      const res = await axios.post(
        `/connect/chat_session/${contactId}/create_ticket/`,
        { issue_description: description }
      );
      const newTicketKey = res.data.ticketKey;
      setTicketKey(newTicketKey);
      // Record the confirmation as a real transcript message (not just a
      // React banner) so it's included when onEnd sends messagesRef.current
      // to /finalize/ — otherwise this never lands in ChatSession.raw_transcript
      // and is invisible when someone later views this session's history.
      setMessages((prev) => [
        ...prev,
        {
          id: `ticket-${newTicketKey}-${Date.now()}`,
          participantRole: 'SYSTEM',
          contentType: 'text/plain',
          content: `Ticket ${newTicketKey} created. Our team will follow up shortly.`,
          absoluteTime: new Date().toISOString(),
        },
      ]);
      // Give the customer time to actually read/copy the ticket number
      // before the widget swaps back to the launcher — a countdown (ticked
      // by the effect below) makes the auto-close visible/expected instead
      // of the chat just vanishing. They can still close early via the
      // existing End Chat/X buttons at any point.
      setCloseCountdown(30);
    } catch (e: any) {
      setTicketError(
        e?.response?.data?.error ||
          "We couldn't create a ticket right now. Please try again or email support@prontohousing.com."
      );
    } finally {
      setSubmittingTicket(false);
    }
  }, [contactId, input, sendMessageText]);

  // Ticks the post-ticket-creation countdown down once per second, closing
  // the chat when it reaches zero. A ref (not the raw handleEndChat
  // closure) is used for the actual close call so this effect doesn't need
  // handleEndChat in its dependency array — handleEndChat's own identity
  // changes with chatEnded, which would otherwise restart the interval.
  const handleEndChatRef = useRef(handleEndChat);
  useEffect(() => {
    handleEndChatRef.current = handleEndChat;
  }, [handleEndChat]);

  useEffect(() => {
    if (closeCountdown === null) {
      return;
    }
    if (closeCountdown <= 0) {
      handleEndChatRef.current();
      return;
    }
    const timer = setTimeout(() => {
      setCloseCountdown((prev) => (prev === null ? null : prev - 1));
    }, 1000);
    return () => clearTimeout(timer);
  }, [closeCountdown]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  // Precompute group boundaries once per messages change rather than per-render-per-item.
  const groupFlags = useMemo(() => {
    return messages.map((msg, index) => {
      const prev = messages[index - 1];
      const next = messages[index + 1];

      const gapExceeds = (a?: ChatMessage, b?: ChatMessage): boolean => {
        if (!a || !b || !a.absoluteTime || !b.absoluteTime) {
          return false;
        }
        return (
          Math.abs(
            new Date(b.absoluteTime).getTime() -
              new Date(a.absoluteTime).getTime()
          ) > GROUP_WINDOW_MS
        );
      };

      const isFirst =
        !prev ||
        prev.participantRole !== msg.participantRole ||
        gapExceeds(prev, msg);
      const isLast =
        !next ||
        next.participantRole !== msg.participantRole ||
        gapExceeds(msg, next);
      return { isFirst, isLast };
    });
  }, [messages]);

  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          width: 360,
          height: 500,
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: PANEL_SHADOW,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Typography color="error" variant="body2" textAlign="center">
            {error}
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (connecting) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          width: 360,
          height: 500,
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: PANEL_SHADOW,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <CircularProgress size={32} />
        </Box>
      </Paper>
    );
  }

  // Dismissed: hidden with a small restore button visible only when
  // dismissible is true. This is for internal support users who want
  // to hide the chat entirely without ending the conversation.
  if (hidden && dismissible) {
    return (
      <Tooltip
        title="Open chat"
        placement="left"
        slotProps={TOOLTIP_POPPER_PROPS}
      >
        <Fab
          color="primary"
          size="small"
          onClick={handleRestore}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: Z_INDEX.MAX,
          }}
        >
          <ChatIcon />
        </Fab>
      </Tooltip>
    );
  }

  // Minimized: same footprint/position as the default FloatingChatButton
  // FAB (bottom:24/right:24) — the hook hides that FAB whenever this
  // widget is mounted at all, so this pill is the only floating element
  // shown. The ChatJS connection above keeps running; nothing here
  // unmounts, so restoring shows an up-to-date conversation.
  if (minimized) {
    return (
      <Tooltip
        title={chatEnded ? 'Chat ended — click to view' : 'Resume chat'}
        placement="left"
        slotProps={TOOLTIP_POPPER_PROPS}
      >
        <Fab
          color="primary"
          size="medium"
          onClick={() => setMinimized(false)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: Z_INDEX.MAX,
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            invisible={unreadCount === 0}
          >
            <ChatIcon />
          </Badge>
        </Fab>
      </Tooltip>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        width: isFullScreen ? { xs: '100vw', sm: 640 } : 360,
        height: isFullScreen ? { xs: '100vh', sm: 640 } : 500,
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        bottom: isFullScreen ? 0 : 16,
        right: isFullScreen ? 0 : 16,
        zIndex: Z_INDEX.MAX,
        // Match the launcher surface: soft radius, clipped children, ambient
        // shadow (see ChatLauncher / PANEL_SHADOW).
        borderRadius: isFullScreen ? 0 : '16px',
        overflow: 'hidden',
        boxShadow: isFullScreen ? 'none' : PANEL_SHADOW,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight={600}>
            Pronto Support
          </Typography>
          {intent === 'report_issue' && (
            <Typography
              variant="caption"
              sx={{ opacity: 0.8, display: 'block', mt: 0.25 }}
            >
              Report an issue
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip
            title={isFullScreen ? 'Exit full screen' : 'Full screen'}
            slotProps={TOOLTIP_POPPER_PROPS}
          >
            <IconButton
              size="small"
              sx={{ color: 'inherit' }}
              onClick={toggleFullScreen}
            >
              {isFullScreen ? (
                <CloseFullscreenIcon fontSize="small" />
              ) : (
                <OpenInFullIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Minimize" slotProps={TOOLTIP_POPPER_PROPS}>
            <IconButton
              size="small"
              sx={{ color: 'inherit' }}
              onClick={() => setMinimized(true)}
            >
              <MinimizeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!chatEnded && (
            <Tooltip
              title={
                intent === 'report_issue'
                  ? ticketSubmitted
                    ? 'Close'
                    : 'Cancel and close'
                  : 'End this conversation'
              }
              slotProps={TOOLTIP_POPPER_PROPS}
            >
              <IconButton
                size="small"
                sx={{
                  color: 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  borderRadius: 4,
                  px: 1,
                  width: 'auto',
                }}
                onClick={handleEndChat}
              >
                {/* report_issue is a ticket form, not a live call — no
                    phone-hangup icon, and "Cancel" reads correctly. */}
                {intent === 'report_issue' ? (
                  <CloseIcon fontSize="small" />
                ) : (
                  <CallEndIcon fontSize="small" />
                )}
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 500, lineHeight: 1 }}
                >
                  {intent === 'report_issue'
                    ? ticketSubmitted
                      ? 'Close'
                      : 'Cancel'
                    : 'End Chat'}
                </Typography>
              </IconButton>
            </Tooltip>
          )}
          {dismissible && (
            <Tooltip title="Hide" slotProps={TOOLTIP_POPPER_PROPS}>
              <IconButton
                size="small"
                sx={{ color: 'inherit' }}
                onClick={handleDismiss}
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
        }}
      >
        {messages.map((msg, index) => {
          const isCustomer = msg.participantRole === 'CUSTOMER';
          const isSystem = msg.participantRole === 'SYSTEM';
          const { isFirst, isLast } = groupFlags[index];

          if (isSystem) {
            return (
              <Typography
                key={msg.id}
                variant="caption"
                color="text.secondary"
                textAlign="center"
                sx={{ my: 0.5, fontStyle: 'italic' }}
              >
                {msg.content}
              </Typography>
            );
          }

          const senderLabel = isCustomer ? 'You' : msg.displayName || 'Agent';

          return (
            <Box
              key={msg.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isCustomer ? 'flex-end' : 'flex-start',
                mt: isFirst ? 1 : 0.25,
              }}
            >
              {isFirst && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ px: 0.5, mb: 0.25 }}
                >
                  {senderLabel}
                </Typography>
              )}
              <Box
                sx={{
                  maxWidth: '80%',
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: isCustomer ? 'primary.light' : 'grey.200',
                  color: isCustomer ? 'primary.contrastText' : 'text.primary',
                  fontSize: '0.875rem',
                }}
              >
                {msg.attachmentId ? (
                  <Box
                    onClick={() =>
                      handleDownloadAttachment(
                        msg.attachmentId as string,
                        msg.attachmentName
                      )
                    }
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      cursor: downloadingAttachmentId ? 'default' : 'pointer',
                      '&:hover': {
                        textDecoration: downloadingAttachmentId
                          ? 'none'
                          : 'underline',
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: 'italic', wordBreak: 'break-word' }}
                    >
                      📎 {msg.attachmentName || 'Attachment'}
                    </Typography>
                    {downloadingAttachmentId === msg.attachmentId ? (
                      <CircularProgress
                        size={12}
                        thickness={6}
                        sx={{ color: 'inherit' }}
                      />
                    ) : (
                      <DownloadIcon sx={{ fontSize: 14 }} />
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2">{msg.content}</Typography>
                )}
              </Box>
              {isLast && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 0.5,
                    mt: 0.25,
                  }}
                >
                  {msg.absoluteTime && (
                    <Tooltip
                      title={fTime(msg.absoluteTime)}
                      slotProps={TOOLTIP_POPPER_PROPS}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {fToNow(msg.absoluteTime)}
                      </Typography>
                    </Tooltip>
                  )}
                  {isCustomer && renderReceiptIcon(msg.status)}
                </Box>
              )}
            </Box>
          );
        })}
        {agentTyping && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ px: 0.5, mt: 0.5, fontStyle: 'italic' }}
          >
            Agent is typing...
          </Typography>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {chatEnded && (
        <Box
          sx={{
            px: 1.5,
            py: 1,
            bgcolor: 'grey.100',
            borderTop: 1,
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            {endedMessage}
          </Typography>
        </Box>
      )}

      {intent === 'report_issue' && !chatEnded && (
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderTop: 1,
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          {ticketKey ? (
            <>
              <Typography variant="body2" color="success.main" fontWeight={600}>
                Ticket {ticketKey} created! Our team will follow up shortly.
              </Typography>
              {closeCountdown !== null && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  Closing in {closeCountdown}s...
                </Typography>
              )}
            </>
          ) : (
            <>
              {ticketError && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: 'block', mb: 0.5 }}
                >
                  {ticketError}
                </Typography>
              )}
              <Button
                fullWidth
                size="small"
                variant="contained"
                startIcon={
                  submittingTicket ? (
                    <CircularProgress size={14} thickness={6} color="inherit" />
                  ) : (
                    <ConfirmationNumberOutlinedIcon fontSize="small" />
                  )
                }
                onClick={handleSubmitTicket}
                disabled={submittingTicket}
              >
                Submit Ticket
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Input */}
      <Box
        sx={{
          p: 1,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 0.5,
          alignItems: 'flex-end',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <Tooltip title="Attach file" slotProps={TOOLTIP_POPPER_PROPS}>
          <IconButton
            size="small"
            onClick={handleAttach}
            disabled={uploadingFile || chatEnded}
          >
            {uploadingFile ? (
              <CircularProgress size={16} thickness={6} />
            ) : (
              <AttachFileIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="Capture screenshot" slotProps={TOOLTIP_POPPER_PROPS}>
          <IconButton
            size="small"
            onClick={handleScreenshot}
            disabled={capturingScreenshot || chatEnded}
          >
            {capturingScreenshot ? (
              <CircularProgress size={16} thickness={6} />
            ) : (
              <ScreenshotMonitorIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder={
            chatEnded
              ? 'Conversation ended'
              : intent === 'report_issue'
              ? 'Describe your issue...'
              : 'Type a message...'
          }
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={chatEnded}
          multiline
          // report_issue is a description-first task — give it a roomy box
          // (grows 3→6 rows). Live-agent chat stays compact (grows to 3).
          minRows={intent === 'report_issue' ? 3 : 1}
          maxRows={intent === 'report_issue' ? 6 : 3}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        <IconButton
          size="small"
          color="primary"
          onClick={sendMessage}
          disabled={!input.trim() || chatEnded}
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
}
